'use client';

import { forwardRef } from 'react';
import Link from 'next/link';
import { clsx } from 'clsx';

import { Props as ButtonProps } from '../DallasButton/DallasButton';
import { Props as DallasLinkProps } from '~/app/_ui/Dallas/Link/DallasLink';

import styles from '../DallasButton/DallasButton.module.scss';

export interface Props
  extends Omit<ButtonProps, 'onClick'>,
    Omit<DallasLinkProps, 'color' | 'underlined'> {}

const DallasLinkButton = forwardRef<HTMLAnchorElement, Props>(
  function DallasLinkButton(
    {
      id,
      href,
      target,
      label,
      onClick,
      className: providedClassName,
      color = 'secondary',
      size = 'regular',
      rounded = false,
      flex = false,
      addOnStart = undefined,
      addOnEnd = undefined,
      tabIndex,
      disabled = false,
      prefetch = false,
      'aria-disabled': ariaDisabled = false,
      ...ariaProps
    }: Props,
    ref,
  ) {
    const isIconOnly =
      !label && ((!!addOnStart && !addOnEnd) || (!addOnStart && !!addOnEnd));
    const shouldRenderAnchorTag =
      href == null ||
      (typeof href === 'string' && (!href.startsWith('/') || href === ''));
    const className = clsx(
      providedClassName,
      styles.root,
      styles[`color-${color}`],
      flex && styles.flex,
      isIconOnly
        ? styles[`iconOnlyPadding_${size}`]
        : styles[`mixedContentPadding_${size}`],
      rounded ? styles.borderRadius_rounded : styles.borderRadius_semiRounded,
    );
    const children = (
      <>
        {addOnStart && <div className={styles.addOnWrapper}>{addOnStart}</div>}
        {label}
        {addOnEnd && <div className={styles.addOnWrapper}>{addOnEnd}</div>}
      </>
    );

    const commonProps = {
      id,
      ref,
      className,
      onClick,
      target,
      children,
      tabIndex,
    };

    const isDisabled = ariaDisabled || disabled;

    return shouldRenderAnchorTag ? (
      <a
        {...ariaProps}
        {...commonProps}
        href={href}
        aria-disabled={isDisabled}
      />
    ) : (
      <Link
        {...ariaProps}
        {...commonProps}
        href={href}
        passHref={true}
        prefetch={prefetch}
        aria-disabled={isDisabled}
      />
    );
  },
);

export default DallasLinkButton;
